import { supabase } from "./supabase";

type RpcResult<T> = { data: T | null; error: { message: string } | null };
export type InventoryLocation = {
  id: string;
  name: string;
  code: string | null;
  city?: string | null;
  state_region?: string | null;
};
export type InventoryProduct = {
  id: string;
  sku: string | null;
  name: string;
  tracking_method?: string;
  unit_of_measure?: string | null;
};
export type BulkPosition = {
  id: string;
  product_id: string;
  sku: string | null;
  product_name: string;
  location_id: string;
  location_name: string;
  fleet_quantity: number;
  on_hand_quantity: number;
  last_counted_at: string | null;
  last_movement_at: string | null;
  updated_at: string;
};
export type UncountedBulkProduct = {
  id: string;
  sku: string | null;
  name: string;
  category: string | null;
  unit_of_measure: string | null;
  tracking_method: string;
  count_status: "opening_count_required";
};
export type InventoryAsset = {
  id: string;
  asset_number: string;
  product_id: string;
  sku: string | null;
  product_name: string;
  home_location_id: string;
  home_location: string | null;
  current_location_id: string | null;
  current_location: string | null;
  lifecycle_status: string;
  availability_status: string;
  operational_status: string;
  condition_status: string;
  primary_identifier: string | null;
  primary_identifier_type: string | null;
  acquisition_date: string | null;
  acquisition_cost: number | null;
  updated_at: string;
};
export type SupplyHold = {
  id: string;
  product_id: string;
  product_name: string;
  sku: string | null;
  location_id: string;
  location_name: string;
  asset_id: string | null;
  hold_type: string;
  quantity: number;
  status: string;
  starts_at: string;
  ends_at: string | null;
  reason: string;
  created_at: string;
};
export type InventoryAssetsWorkspace = {
  contract_version: string;
  generated_at: string;
  organization_id: string;
  capabilities: {
    view: boolean;
    manage: boolean;
    assets_manage: boolean;
    availability_view: boolean;
  };
  metrics: {
    serialized_assets: number;
    available_assets: number;
    bulk_positions: number;
    uncounted_bulk_products: number;
    active_holds: number;
    active_transfers: number;
    pending_adjustments: number;
  };
  locations: InventoryLocation[];
  products: InventoryProduct[];
  bulk_positions: BulkPosition[];
  uncounted_bulk_products: UncountedBulkProduct[];
  assets: InventoryAsset[];
  holds: SupplyHold[];
};
export type AvailabilityRow = {
  organization_id: string;
  location_id: string;
  product_id: string;
  requested_start_at: string;
  requested_end_at: string;
  location_reserved_quantity: number;
  unassigned_reserved_quantity: number;
  total_known_demand_quantity: number;
  overlapping_reservation_count: number;
  earliest_release_at: string | null;
  physical_supply_quantity: number;
  available_quantity: number;
  shortage_quantity: number;
  availability_state: string;
};
export type AssetIdentifier = {
  id: string;
  identifier_type: string;
  identifier_value: string;
  is_primary: boolean;
  is_active: boolean;
  issued_at: string | null;
};
export type AssetStatusHistory = {
  id: string;
  prior_lifecycle_status: string | null;
  new_lifecycle_status: string | null;
  prior_availability_status: string | null;
  new_availability_status: string | null;
  prior_operational_status: string | null;
  new_operational_status: string | null;
  prior_condition_status: string | null;
  new_condition_status: string | null;
  reason: string | null;
  occurred_at: string;
};
export type AssetLocationHistory = {
  id: string;
  from_location_id: string | null;
  from_location: string | null;
  to_location_id: string | null;
  to_location: string | null;
  location_scope: string | null;
  movement_type: string;
  notes: string | null;
  occurred_at: string;
};
export type AssetMovement = {
  id: string;
  movement_type: string;
  from_location: string | null;
  to_location: string | null;
  reason: string | null;
  occurred_at: string;
};
export type InventoryAssetDetail = {
  contract_version: string;
  generated_at: string;
  capabilities: { manage: boolean };
  asset: InventoryAsset & {
    notes?: string | null;
    ownership_type?: string | null;
  };
  identifiers: AssetIdentifier[];
  status_history: AssetStatusHistory[];
  location_history: AssetLocationHistory[];
  movements: AssetMovement[];
};

export type TransferItem = {
  id: string;
  transfer_number: string;
  from_location_id: string;
  from_location: string;
  to_location_id: string;
  to_location: string;
  status: string;
  planned_departure_at: string | null;
  planned_arrival_at: string | null;
  actual_departure_at: string | null;
  actual_arrival_at: string | null;
  reason: string | null;
  line_count: number;
  asset_count: number;
  updated_at: string;
};
export type TransfersWorkspace = {
  contract_version: string;
  generated_at: string;
  organization_id: string;
  capabilities: { view: boolean; manage: boolean };
  metrics: {
    draft: number;
    requested: number;
    approved: number;
    in_transit: number;
  };
  status_options: string[];
  locations: InventoryLocation[];
  items: TransferItem[];
};
export type TransferLine = {
  id: string;
  product_id: string;
  sku: string | null;
  product_name: string;
  quantity: number;
  tracking_method: string;
  notes: string | null;
};
export type TransferAsset = {
  id: string;
  asset_id: string;
  asset_number: string;
  product_id: string;
  product_name: string;
  sku: string | null;
  condition_status: string;
  availability_status: string;
};
export type EligibleTransferAsset = {
  id: string;
  asset_number: string;
  product_id: string;
  product_name: string;
  sku: string | null;
  availability_status: string;
  condition_status: string;
};
export type TransferDetail = {
  contract_version: string;
  generated_at: string;
  capabilities: { manage: boolean };
  transfer: TransferItem & { notes?: string | null };
  allowed_actions: string[];
  products: InventoryProduct[];
  lines: TransferLine[];
  assets: TransferAsset[];
  eligible_assets: EligibleTransferAsset[];
};

export type AdjustmentItem = {
  id: string;
  adjustment_number: string;
  product_id: string;
  sku: string | null;
  product_name: string;
  location_id: string;
  location_name: string;
  adjustment_type: string;
  fleet_quantity_delta: number;
  on_hand_quantity_delta: number;
  status: string;
  reason: string;
  decision_reason: string | null;
  requested_by_user_id: string | null;
  approved_by_user_id: string | null;
  requested_at: string | null;
  approved_at: string | null;
  applied_at: string | null;
  updated_at: string;
};
export type AdjustmentsWorkspace = {
  contract_version: string;
  generated_at: string;
  organization_id: string;
  capabilities: {
    view: boolean;
    request: boolean;
    approve: boolean;
    manage: boolean;
  };
  metrics: {
    draft: number;
    pending: number;
    approved: number;
    applied: number;
  };
  status_options: string[];
  locations: InventoryLocation[];
  products: InventoryProduct[];
  items: AdjustmentItem[];
};
export type TentAssemblyProduct = {
  profile_id: string;
  product_id: string;
  sku: string | null;
  name: string;
  configuration_type: string;
  width_m: number | null;
  minimum_length_m: number;
  maximum_length_m: number | null;
  default_length_m: number | null;
  length_increment_m: number;
  display_unit: "m" | "ft" | null;
  allowed_lengths_m: number[] | null;
};
export type TentAssemblyComponentProduct = {
  id: string;
  sku: string | null;
  name: string;
  category: string | null;
  unit_of_measure: string | null;
  tracking_method: string;
  inventory_tracked: boolean;
};
export type TentAssemblyComponent = {
  id: string;
  product_id: string;
  sku: string | null;
  name: string;
  category: string | null;
  unit_of_measure: string | null;
  tracking_method: string;
  inventory_tracked: boolean;
  quantity: number;
  component_role: string;
  is_optional: boolean;
  notes: string | null;
};
export type TentAssembly = {
  id: string;
  profile_id: string;
  product_id: string;
  sku: string | null;
  name: string;
  width_m: number | null;
  configured_length_m: number;
  display_unit: "m" | "ft" | null;
  source_system: string;
  source_package_key: string | null;
  source_package_name: string | null;
  is_active: boolean;
  notes: string | null;
  component_count: number;
  components: TentAssemblyComponent[];
};
export type TentAssembliesWorkspace = {
  contract_version: string;
  organization_id: string;
  capabilities: { manage: boolean };
  configured_products: TentAssemblyProduct[];
  component_products: TentAssemblyComponentProduct[];
  assemblies: TentAssembly[];
};
export type SerializedPartCategory = {
  id: string;
  name: string;
  parent_category_id: string | null;
  parent_name: string | null;
  label: string;
};
export type SerializedPartSetup = {
  contract_version: string;
  organization_id: string;
  categories: SerializedPartCategory[];
};
export type SerializedPartCreateResult = {
  product_id: string;
  asset_id: string;
  category_id: string;
};
export type InventoryEditableProduct = {
  id: string;
  sku: string;
  name: string;
  category_id: string | null;
  category: string | null;
  description: string | null;
  unit_of_measure: string;
  tracking_method: string;
  inventory_tracked: boolean;
  status: string;
  updated_at: string;
};
export type InventoryProductEditorWorkspace = {
  contract_version: string;
  organization_id: string;
  capabilities: { manage: boolean };
  categories: SerializedPartCategory[];
  products: InventoryEditableProduct[];
};

async function rpc<T>(
  name: string,
  args: Record<string, unknown>,
): Promise<RpcResult<T>> {
  const r = await supabase.rpc(name, args);
  return {
    data: (r.data ?? null) as T | null,
    error: r.error ? { message: r.error.message } : null,
  };
}
export const inventoryApi = {
  assets: (
    organizationId: string,
    search: string | null,
    locationId: string | null,
    tracking: string | null,
  ) =>
    rpc<InventoryAssetsWorkspace>("get_inventory_assets_workspace", {
      p_organization_id: organizationId,
      p_search: search,
      p_location_id: locationId,
      p_tracking_method: tracking,
    }),
  assetDetail: (assetId: string) =>
    rpc<InventoryAssetDetail>("get_inventory_asset_detail", {
      p_asset_id: assetId,
    }),
  availability: async (
    organizationId: string,
    locationId: string,
    productId: string,
    start: string,
    end: string,
  ): Promise<RpcResult<AvailabilityRow>> => {
    const r = await supabase.rpc("get_rental_availability_window", {
      p_organization_id: organizationId,
      p_location_id: locationId,
      p_product_id: productId,
      p_start_at: start,
      p_end_at: end,
      p_exclude_reservation_id: null,
    });
    const row = Array.isArray(r.data) ? (r.data[0] ?? null) : r.data;
    return {
      data: row as AvailabilityRow | null,
      error: r.error ? { message: r.error.message } : null,
    };
  },
  createAsset: (args: {
    organizationId: string;
    productId: string;
    locationId: string;
    serial: string | null;
    barcode: string | null;
    qr: string | null;
    rfid: string | null;
    acquisitionDate: string | null;
    acquisitionCost: number | null;
    notes: string | null;
  }) =>
    rpc<string>("create_serialized_asset", {
      p_organization_id: args.organizationId,
      p_product_id: args.productId,
      p_home_location_id: args.locationId,
      p_serial_number: args.serial,
      p_barcode: args.barcode,
      p_qr_code: args.qr,
      p_rfid_epc: args.rfid,
      p_acquisition_date: args.acquisitionDate,
      p_acquisition_cost: args.acquisitionCost,
      p_notes: args.notes,
    }),
  addIdentifier: (
    assetId: string,
    type: string,
    value: string,
    primary: boolean,
  ) =>
    rpc<string>("add_asset_identifier", {
      p_asset_id: assetId,
      p_identifier_type: type,
      p_identifier_value: value,
      p_is_primary: primary,
    }),
  transitionAsset: (
    assetId: string,
    args: {
      lifecycle: string | null;
      availability: string | null;
      operational: string | null;
      condition: string | null;
      reason: string;
    },
  ) =>
    rpc<string>("transition_asset_state", {
      p_asset_id: assetId,
      p_lifecycle_status: args.lifecycle,
      p_availability_status: args.availability,
      p_operational_status: args.operational,
      p_condition_status: args.condition,
      p_reason: args.reason,
      p_source_entity_type: "frontend",
      p_source_entity_id: null,
    }),
  placeHold: (args: {
    organizationId: string;
    productId: string;
    locationId: string;
    assetId: string | null;
    holdType: string;
    quantity: number;
    startsAt: string | null;
    endsAt: string | null;
    reason: string;
  }) =>
    rpc<string>("place_inventory_supply_hold", {
      p_organization_id: args.organizationId,
      p_product_id: args.productId,
      p_location_id: args.locationId,
      p_asset_id: args.assetId,
      p_hold_type: args.holdType,
      p_quantity: args.quantity,
      p_starts_at: args.startsAt,
      p_ends_at: args.endsAt,
      p_reason: args.reason,
    }),
  releaseHold: (holdId: string, reason: string | null) =>
    rpc<string>("release_inventory_supply_hold", {
      p_hold_id: holdId,
      p_reason: reason,
    }),
  transfers: (
    organizationId: string,
    status: string | null,
    search: string | null,
  ) =>
    rpc<TransfersWorkspace>("get_inventory_transfers_workspace", {
      p_organization_id: organizationId,
      p_status: status,
      p_search: search,
    }),
  transferDetail: (transferId: string) =>
    rpc<TransferDetail>("get_inventory_transfer_detail", {
      p_transfer_id: transferId,
    }),
  createTransfer: (args: {
    organizationId: string;
    fromLocationId: string;
    toLocationId: string;
    departure: string | null;
    arrival: string | null;
    reason: string | null;
    notes: string | null;
  }) =>
    rpc<string>("create_inventory_transfer", {
      p_organization_id: args.organizationId,
      p_from_location_id: args.fromLocationId,
      p_to_location_id: args.toLocationId,
      p_planned_departure_at: args.departure,
      p_planned_arrival_at: args.arrival,
      p_reason: args.reason,
      p_notes: args.notes,
    }),
  addTransferLine: (transferId: string, productId: string, quantity: number) =>
    rpc<string>("add_inventory_transfer_line", {
      p_transfer_id: transferId,
      p_product_id: productId,
      p_quantity: quantity,
    }),
  addTransferAsset: (transferId: string, assetId: string) =>
    rpc<string>("add_inventory_transfer_asset", {
      p_transfer_id: transferId,
      p_asset_id: assetId,
    }),
  requestTransfer: (id: string) =>
    rpc<string>("request_inventory_transfer", { p_transfer_id: id }),
  approveTransfer: (id: string) =>
    rpc<string>("approve_inventory_transfer", { p_transfer_id: id }),
  dispatchTransfer: (id: string) =>
    rpc<string>("dispatch_inventory_transfer", { p_transfer_id: id }),
  receiveTransfer: (id: string) =>
    rpc<string>("receive_inventory_transfer", { p_transfer_id: id }),
  cancelTransfer: (id: string, reason: string) =>
    rpc<string>("cancel_inventory_transfer", {
      p_transfer_id: id,
      p_reason: reason,
    }),
  adjustments: (
    organizationId: string,
    status: string | null,
    search: string | null,
  ) =>
    rpc<AdjustmentsWorkspace>("get_inventory_adjustments_workspace", {
      p_organization_id: organizationId,
      p_status: status,
      p_search: search,
    }),
  createAdjustment: (args: {
    organizationId: string;
    productId: string;
    locationId: string;
    type: string;
    fleetDelta: number;
    onHandDelta: number;
    reason: string;
    notes: string | null;
  }) =>
    rpc<string>("create_inventory_adjustment", {
      p_organization_id: args.organizationId,
      p_product_id: args.productId,
      p_location_id: args.locationId,
      p_adjustment_type: args.type,
      p_fleet_delta: args.fleetDelta,
      p_on_hand_delta: args.onHandDelta,
      p_reason: args.reason,
      p_notes: args.notes,
    }),
  submitAdjustment: (id: string) =>
    rpc<string>("submit_inventory_adjustment", { p_adjustment_id: id }),
  decideAdjustment: (id: string, approve: boolean, reason: string) =>
    rpc<string>("decide_inventory_adjustment", {
      p_adjustment_id: id,
      p_approve: approve,
      p_decision_reason: reason,
    }),
  applyAdjustment: (id: string) =>
    rpc<string>("apply_inventory_adjustment", { p_adjustment_id: id }),
  tentAssemblies: (organizationId: string) =>
    rpc<TentAssembliesWorkspace>("get_inventory_tent_assemblies_workspace", {
      p_organization_id: organizationId,
    }),
  saveTentAssembly: (a: {
    organizationId: string;
    profileId: string;
    configuredLengthM: number;
    packageName: string;
    notes: string | null;
  }) =>
    rpc<string>("upsert_inventory_tent_assembly", {
      p_organization_id: a.organizationId,
      p_profile_id: a.profileId,
      p_configured_length_m: a.configuredLengthM,
      p_package_name: a.packageName,
      p_notes: a.notes,
    }),
  saveTentAssemblyComponent: (a: {
    organizationId: string;
    configurationSizeId: string;
    componentProductId: string;
    quantity: number;
    componentRole: string;
    isOptional: boolean;
    notes: string | null;
  }) =>
    rpc<string>("upsert_inventory_tent_assembly_component", {
      p_organization_id: a.organizationId,
      p_configuration_size_id: a.configurationSizeId,
      p_component_product_id: a.componentProductId,
      p_quantity: a.quantity,
      p_component_role: a.componentRole,
      p_is_optional: a.isOptional,
      p_notes: a.notes,
    }),
  removeTentAssemblyComponent: (organizationId: string, componentId: string) =>
    rpc<boolean>("delete_inventory_tent_assembly_component", {
      p_organization_id: organizationId,
      p_component_id: componentId,
    }),
  serializedPartSetup: (organizationId: string) =>
    rpc<SerializedPartSetup>("get_inventory_serialized_part_setup", {
      p_organization_id: organizationId,
    }),
  createSerializedPart: (a: {
    organizationId: string;
    sku: string;
    name: string;
    homeLocationId: string;
    identifierType: string;
    identifierValue: string;
    categoryId: string | null;
    newCategoryName: string | null;
    description: string | null;
    unitOfMeasure: string;
    acquisitionDate: string | null;
    acquisitionCost: number | null;
    notes: string | null;
  }) =>
    rpc<SerializedPartCreateResult>(
      "create_inventory_serialized_part_and_asset",
      {
        p_organization_id: a.organizationId,
        p_sku: a.sku,
        p_name: a.name,
        p_home_location_id: a.homeLocationId,
        p_identifier_type: a.identifierType,
        p_identifier_value: a.identifierValue,
        p_category_id: a.categoryId,
        p_new_category_name: a.newCategoryName,
        p_description: a.description,
        p_unit_of_measure: a.unitOfMeasure,
        p_acquisition_date: a.acquisitionDate,
        p_acquisition_cost: a.acquisitionCost,
        p_notes: a.notes,
      },
    ),
  productEditor: (organizationId: string) =>
    rpc<InventoryProductEditorWorkspace>(
      "get_inventory_product_editor_workspace",
      { p_organization_id: organizationId },
    ),
  updateProduct: (a: {
    organizationId: string;
    productId: string;
    sku: string;
    name: string;
    categoryId: string | null;
    newCategoryName: string | null;
    description: string | null;
    unitOfMeasure: string;
    status: string;
  }) =>
    rpc<{ product_id: string; category_id: string }>(
      "update_inventory_product_catalog_record",
      {
        p_organization_id: a.organizationId,
        p_product_id: a.productId,
        p_sku: a.sku,
        p_name: a.name,
        p_category_id: a.categoryId,
        p_new_category_name: a.newCategoryName,
        p_description: a.description,
        p_unit_of_measure: a.unitOfMeasure,
        p_status: a.status,
      },
    ),
};
